import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import JsonLd from '@/components/feature/JsonLd';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { getComic } from '@/mocks/comics';
import { getChapters } from '@/mocks/chapters';
import { readingPages } from '@/mocks/readingPages';

export default function ReadingPage() {
  const { id = '', num = '1' } = useParams<{ id: string; num: string }>();
  const comic = getComic(id);
  const chapters = id ? getChapters(id) : [];
  const currentIndex = Math.max(0, chapters.findIndex((c) => c.id === num));
  const chapter = chapters[currentIndex];
  const [loadedPages, setLoadedPages] = useState(0);

  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : undefined;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : undefined;

  const pageItems = useMemo(() => readingPages.slice(0, 6), []);

  useSeo({
    title: comic && chapter
      ? `《${comic.title}》${chapter.title}-免费阅读韩国漫画-5177漫画网`
      : '漫画阅读-5177漫画网',
    description: comic && chapter
      ? `${comic.title}${chapter.title}中文免费阅读，5177漫画网提供全集中文韩漫在线浏览。`
      : '5177漫画网免费韩国漫画阅读',
    keywords: `${comic?.title ?? ''}漫画,${chapter?.title ?? ''},韩国漫画,免费阅读,5177漫画网`,
    ogType: 'article',
    ogImage: comic?.cover,
    canonicalPath: `/comic/${id}/chapter/${num}`,
  });

  if (!comic || !chapter) {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-book-2-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-4 font-display text-2xl text-foreground-950">没有找到该章节</h1>
          <Link
            to={comic ? `/comic/${comic.id}` : '/category/all'}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600"
          >
            {comic ? '返回漫画详情' : '浏览漫画大全'}
          </Link>
        </div>
      </div>
    );
  }

  const readLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/comic/${id}/chapter/${num}#page`,
    url: `${SITE_URL}/comic/${id}/chapter/${num}`,
    name: `《${comic.title}》${chapter.title}`,
    description: `${comic.title}${chapter.title}中文免费阅读。`,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': `${SITE_URL}/comic/${id}#comic` },
  };

  return (
    <div className="min-h-screen bg-background-100">
      <JsonLd data={readLd} />
      <ScrollToTop />

      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={`/comic/${comic.id}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200"
              aria-label="返回漫画详情"
            >
              <i className="ri-arrow-left-line text-lg" aria-hidden="true" />
            </Link>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground-900">
                《{comic.title}》{chapter.title}
              </p>
              <p className="text-xs text-foreground-500">
                第{currentIndex + 1}话 / 共{chapters.length}话
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={chapter.id}
              onChange={(e) => {
                window.REACT_APP_NAVIGATE(`/comic/${comic.id}/chapter/${e.target.value}`);
              }}
              className="h-9 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-300"
              aria-label="选择章节"
            >
              {chapters.map((c, i) => (
                <option key={c.id} value={c.id}>
                  第{i + 1}话 {c.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 md:px-6 py-6 md:py-8">
        <div className="mx-auto flex items-center justify-center gap-3">
          {prevChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${prevChapter.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-background-50 border border-background-200 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-skip-back-line" aria-hidden="true" />
              上一话
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-background-200/60 px-4 py-2 text-sm text-foreground-400 whitespace-nowrap cursor-not-allowed">
              <i className="ri-skip-back-line" aria-hidden="true" />
              已是第一话
            </span>
          )}

          <h1 className="hidden sm:block font-display text-lg md:text-xl text-foreground-950">{chapter.title}</h1>

          {nextChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${nextChapter.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              下一话
              <i className="ri-skip-forward-line" aria-hidden="true" />
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-background-200/60 px-4 py-2 text-sm text-foreground-400 whitespace-nowrap cursor-not-allowed">
              已是最后一话
              <i className="ri-skip-forward-line" aria-hidden="true" />
            </span>
          )}
        </div>

        <div className="mx-auto mt-6 max-w-3xl">
          {pageItems.map((src, index) => (
            <figure key={src} className="overflow-hidden rounded-lg bg-background-50 mb-3">
              <img
                src={src}
                alt={`《${comic.title}》${chapter.title}漫画阅读页 第${index + 1}页`}
                title={`${comic.title}-${chapter.title} 第${index + 1}页`}
                className="w-full"
                onLoad={() => setLoadedPages((n) => n + 1)}
              />
            </figure>
          ))}

          <div className="mt-4 text-center text-xs text-foreground-500">
            已加载 {loadedPages > 0 ? `${loadedPages} 页漫画内容` : '漫画内容加载中…'} · 建议在 Wi-Fi 环境下阅读
          </div>
        </div>

        <div className="mx-auto mt-8 flex max-w-3xl items-center justify-center gap-3">
          {prevChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${prevChapter.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-background-50 border border-background-200 px-6 py-2.5 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-skip-back-line" aria-hidden="true" />
              上一话
            </Link>
          ) : null}
          <Link
            to={`/comic/${comic.id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-secondary-500 px-6 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-secondary-600"
          >
            <i className="ri-list-unordered" aria-hidden="true" />
            返回章节列表
          </Link>
          {nextChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${nextChapter.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              下一话
              <i className="ri-skip-forward-line" aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      </main>
    </div>
  );
}